import { useEffect, useMemo, useState } from "react";
import Papa from "papaparse";
import { useSDK } from "../lib/sdk-context";
import { brandApi } from "../lib/brand-api";
import { useExposure, type ExposureKey } from "../lib/use-exposure";
import { Button } from "../components/ui/Button";
import { Input, Label } from "../components/ui/Input";
import { Badge } from "../components/ui/Badge";
import { useToast } from "../components/ui/Toast";

type Kind = "cm" | "wtb" | "adspend" | "supplemental";

type AdSpendVendor = "dutchie" | "iheartjane" | "surfside";

const AD_SPEND_VENDORS: { value: AdSpendVendor; label: string }[] = [
  { value: "dutchie", label: "Dutchie" },
  { value: "iheartjane", label: "iHeartJane" },
  { value: "surfside", label: "Surfside" },
];

interface DetectResult {
  filename: string | null;
  detected_level: "store" | "account";
  vendor_guess: AdSpendVendor | null;
  row_count: number;
  columns: { name: string; sample: string | null; role_hint: string }[];
}

interface IngestRow {
  filename: string | null;
  rows_ingested: number;
  rows_seen: number;
  detected_level: "store" | "account";
  vendor_account_count: number;
  run_id: string;
  match?: Record<string, unknown> | null;
}

interface PrivateCol {
  source_column: string;
  display_label: string;
  show_in_ui: boolean;
  expose_via_api: boolean;
  data_type: string;
}

function detectType(value: string | undefined, header?: string): string {
  const v = (value ?? "").trim();
  const h = (header ?? "").toLowerCase();
  if (!v) return "text";
  // "phone" is HEADER-driven — never inferred from a dashed number alone, or an
  // EIN / license number / other formatted id would false-positive as a phone.
  if (/\b(phone|tel|telephone|fax|mobile|cell)\b/.test(h) && /^\+?[\d().\-\s]{7,}$/.test(v)) return "phone";
  if (/^-?\d+(\.\d+)?$/.test(v)) return "number"; // plain integer/decimal, no separators
  if (/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) return "email";
  // Dates only when the value actually looks like one (ISO-ish); Date.parse alone
  // is too loose and would mislabel formatted ids.
  if (/^\d{4}[-/]\d{1,2}[-/]\d{1,2}/.test(v) && !Number.isNaN(Date.parse(v))) return "date";
  // Everything else — incl. EINs (XX-XXXXXXX), license numbers, zips w/ leading
  // zeros — stays text so the exact string (em-dash and all) is preserved.
  return "text";
}

export default function OnboardWizard({ slug, initialKind }: { slug: string; initialKind?: Kind }) {
  const { sdk } = useSDK();
  const toast = useToast();
  // A provided initialKind (from /brand/:slug/upload/<kind>) skips the picker.
  // For adspend, step>=2 hands off to its own sub-flow (AdSpendUpload).
  const [step, setStep] = useState(initialKind ? 2 : 1);
  const [kind, setKind] = useState<Kind>(initialKind ?? "cm");
  const [file, setFile] = useState<File | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [preview, setPreview] = useState<Record<string, string>[]>([]);
  const [keyColumn, setKeyColumn] = useState("");
  const [repEmailCol, setRepEmailCol] = useState("");
  const [repNameCol, setRepNameCol] = useState("");
  const [matchScope, setMatchScope] = useState<"universe" | "mine">("universe");
  const [privCols, setPrivCols] = useState<PrivateCol[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const structural = useMemo(
    () => new Set([keyColumn, repEmailCol, repNameCol].filter(Boolean)),
    [keyColumn, repEmailCol, repNameCol]
  );

  const onFile = (f: File) => {
    setFile(f);
    Papa.parse<Record<string, string>>(f, {
      header: true,
      skipEmptyLines: true,
      preview: 4,
      transformHeader: (h) => h.trim(),
      complete: (res) => {
        const hdrs = res.meta.fields ?? [];
        setHeaders(hdrs);
        setPreview(res.data.slice(0, 3));
        if (kind === "supplemental") {
          // No key column — resolution is on the license number (auto-detected
          // server-side). Go straight to the per-column expose step. Every
          // column is opt-out by default; sensitive ones stay hidden unless
          // ticked (rule: force an explicit check to expose).
          const sample = (res.data[0] ?? {}) as Record<string, string>;
          setPrivCols(
            hdrs.map((h) => ({
              source_column: h,
              display_label: h,
              show_in_ui: true,
              expose_via_api: false,
              data_type: detectType(sample[h], h),
            }))
          );
          setStep(5);
        } else {
          setStep(3);
        }
      },
      error: (err) => {
        sdk?.reportError?.({ title: "CSV parse failed", description: err.message, severity: "low" });
        toast.push(`Could not parse CSV: ${err.message}`, "error");
      },
    });
  };

  // Build private-column rows from non-structural headers when entering step 5.
  const enterTagStep = () => {
    const sample = preview[0] ?? {};
    const cols: PrivateCol[] = headers
      .filter((h) => !structural.has(h))
      .map((h) => ({
        source_column: h,
        display_label: h,
        show_in_ui: true,
        expose_via_api: false,
        data_type: detectType(sample[h], h),
      }));
    setPrivCols(cols);
    setStep(5);
  };

  const submit = async () => {
    if (!file) return;
    setSubmitting(true);
    try {
      const config = {
        ...(kind === "cm" ? { account_column: keyColumn } : { store_name_column: keyColumn }),
        ...(kind === "cm" && repEmailCol ? { sales_rep_email_column: repEmailCol } : {}),
        ...(kind === "cm" && repNameCol ? { sales_rep_name_column: repNameCol } : {}),
        match_scope: matchScope,
        private_columns: privCols.map((c) => ({
          source_column: c.source_column,
          display_label: c.display_label,
          show_in_ui: c.show_in_ui,
          expose_via_api: c.expose_via_api,
        })),
      };
      const form = new FormData();
      form.append("file", file);
      form.append("kind", kind);
      form.append("config", JSON.stringify(config));
      if (kind === "supplemental") {
        // Supplemental resolves on the license number (license_exact) via its
        // own endpoint; only the per-column expose choices matter in config.
        const r = await brandApi.postForm(
          `/api/brand/${encodeURIComponent(slug)}/address-book/import`,
          form
        );
        toast.push(`Uploaded ${r.row_count} supplemental rows — resolved on license number.`, "success");
        setStep(1);
      } else {
        const r = await brandApi.postForm(`/api/brand/${encodeURIComponent(slug)}/upload`, form);
        toast.push(
          `Uploaded ${r.rows_total} rows — ${r.rows_auto_matched} auto-matched, ${r.rows_pending_review} need review, ${r.rows_unmatched} unmatched`,
          "success"
        );
        window.location.hash = `#/brand/${encodeURIComponent(slug)}/review`;
      }
    } catch (err) {
      sdk?.reportError?.({ title: "Upload failed", description: (err as Error).message, severity: "high" });
      toast.push(`Upload failed: ${(err as Error).message}`, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const stepTitle = ["", "Dataset type", "Upload CSV", "Pick the key column", "Optional sales-rep columns", "Tag your columns"][step];

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-gray-900">Onboarding — {slug}</h2>
        {!(kind === "adspend" && step >= 2) && (
          <p className="text-sm text-gray-500 mt-1">Step {step === 4 && kind === "wtb" ? 3 : step} · {stepTitle}</p>
        )}
      </div>

      {kind === "adspend" && step >= 2 && (
        <AdSpendUpload slug={slug} onBack={initialKind ? undefined : () => setStep(1)} />
      )}

      {step === 1 && <ApiExposurePanel slug={slug} />}

      {step === 1 && <DataContractPanel slug={slug} />}

      {step === 1 && (
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm space-y-4">
          <p className="text-sm text-gray-600">Which kind of data are you uploading?</p>
          <div className="flex flex-wrap gap-3">
            <label className={`flex-1 min-w-[12rem] cursor-pointer rounded-lg border p-4 ${kind === "cm" ? "border-accent-500 bg-accent-50" : "border-gray-200"}`}>
              <input type="radio" name="kind" className="mr-2" checked={kind === "cm"} onChange={() => setKind("cm")} />
              <span className="font-medium">Customer Master (CM)</span>
              <p className="text-xs text-gray-500 mt-1">Your accounts / stores list.</p>
            </label>
            <label className={`flex-1 min-w-[12rem] cursor-pointer rounded-lg border p-4 ${kind === "wtb" ? "border-accent-500 bg-accent-50" : "border-gray-200"}`}>
              <input type="radio" name="kind" className="mr-2" checked={kind === "wtb"} onChange={() => setKind("wtb")} />
              <span className="font-medium">Where-to-Buy (WTB)</span>
              <p className="text-xs text-gray-500 mt-1">Public store locator feed.</p>
            </label>
            <label className={`flex-1 min-w-[12rem] cursor-pointer rounded-lg border p-4 ${kind === "adspend" ? "border-accent-500 bg-accent-50" : "border-gray-200"}`}>
              <input type="radio" name="kind" className="mr-2" checked={kind === "adspend"} onChange={() => setKind("adspend")} />
              <span className="font-medium">Ad Spend</span>
              <p className="text-xs text-gray-500 mt-1">Dutchie / iHeartJane / Surfside exports.</p>
            </label>
            <label className={`flex-1 min-w-[12rem] cursor-pointer rounded-lg border p-4 ${kind === "supplemental" ? "border-accent-500 bg-accent-50" : "border-gray-200"}`}>
              <input type="radio" name="kind" className="mr-2" checked={kind === "supplemental"} onChange={() => setKind("supplemental")} />
              <span className="font-medium">Supplemental data</span>
              <p className="text-xs text-gray-500 mt-1">Account books / address lists / license lists. Resolved on the license number.</p>
            </label>
          </div>
          <Button variant="primary" onClick={() => setStep(2)}>Continue</Button>
        </div>
      )}

      {kind !== "adspend" && step === 2 && (
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm space-y-4">
          <Label htmlFor="csv">Drop your CSV</Label>
          <input id="csv" type="file" accept=".csv,text/csv" onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} />
          <Button variant="ghost" onClick={() => setStep(1)}>Back</Button>
        </div>
      )}

      {kind !== "adspend" && step === 3 && (
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm space-y-4">
          <Label htmlFor="key">
            {kind === "cm" ? "Which column identifies your account/store?" : "Which column is the store name?"}
          </Label>
          <select id="key" className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" value={keyColumn} onChange={(e) => setKeyColumn(e.target.value)}>
            <option value="">— choose a column —</option>
            {headers.map((h) => <option key={h} value={h}>{h}</option>)}
          </select>
          {preview.length > 0 && keyColumn && (
            <p className="text-xs text-gray-500">e.g. “{preview[0][keyColumn]}”</p>
          )}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setStep(2)}>Back</Button>
            <Button variant="primary" disabled={!keyColumn} onClick={() => (kind === "cm" ? setStep(4) : enterTagStep())}>Continue</Button>
          </div>
        </div>
      )}

      {step === 4 && kind === "cm" && (
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm space-y-4">
          <p className="text-sm text-gray-600">Optionally map sales-rep columns to auto-populate reps (max 3 per account).</p>
          <div>
            <Label htmlFor="repEmail">Sales rep email column (optional)</Label>
            <select id="repEmail" className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" value={repEmailCol} onChange={(e) => setRepEmailCol(e.target.value)}>
              <option value="">— none —</option>
              {headers.filter((h) => h !== keyColumn).map((h) => <option key={h} value={h}>{h}</option>)}
            </select>
          </div>
          <div>
            <Label htmlFor="repName">Sales rep name column (optional, paired with email)</Label>
            <select id="repName" className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" value={repNameCol} onChange={(e) => setRepNameCol(e.target.value)} disabled={!repEmailCol}>
              <option value="">— none —</option>
              {headers.filter((h) => h !== keyColumn && h !== repEmailCol).map((h) => <option key={h} value={h}>{h}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setStep(3)}>Back</Button>
            <Button variant="primary" onClick={enterTagStep}>Continue</Button>
          </div>
        </div>
      )}

      {kind !== "adspend" && step === 5 && (
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm space-y-4">
          <div>
            <Label htmlFor="scope">Match scope</Label>
            <select id="scope" className="rounded-md border border-gray-300 px-3 py-2 text-sm" value={matchScope} onChange={(e) => setMatchScope(e.target.value as "universe" | "mine")}>
              <option value="universe">Entire California universe</option>
              <option value="mine">Only retailers I've already matched</option>
            </select>
          </div>
          {kind === "wtb" && <WtbExposeToggle slug={slug} />}
          <p className="text-sm text-gray-600">Tag your remaining columns. These stay private to your brand.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b">
                  <th className="py-2 pr-4">Column</th>
                  <th className="py-2 pr-4">Display label</th>
                  <th className="py-2 pr-4">Type</th>
                  <th className="py-2 pr-4">Show in UI</th>
                  {kind !== "wtb" && <th className="py-2 pr-4">Expose via API</th>}
                </tr>
              </thead>
              <tbody>
                {privCols.map((c, i) => (
                  <tr key={c.source_column} className="border-b last:border-0">
                    <td className="py-2 pr-4 font-medium text-gray-700">{c.source_column}</td>
                    <td className="py-2 pr-4">
                      <Input value={c.display_label} onChange={(e) => {
                        const next = [...privCols]; next[i] = { ...c, display_label: e.target.value }; setPrivCols(next);
                      }} />
                    </td>
                    <td className="py-2 pr-4"><Badge variant="neutral">{c.data_type}</Badge></td>
                    <td className="py-2 pr-4">
                      <input type="checkbox" checked={c.show_in_ui} onChange={(e) => {
                        const next = [...privCols]; next[i] = { ...c, show_in_ui: e.target.checked }; setPrivCols(next);
                      }} />
                    </td>
                    {kind !== "wtb" && (
                      <td className="py-2 pr-4">
                        <input type="checkbox" checked={c.expose_via_api} onChange={(e) => {
                          const next = [...privCols]; next[i] = { ...c, expose_via_api: e.target.checked }; setPrivCols(next);
                        }} />
                      </td>
                    )}
                  </tr>
                ))}
                {privCols.length === 0 && (
                  <tr><td colSpan={kind === "wtb" ? 4 : 5} className="py-3 text-gray-400">No additional columns.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setStep(kind === "cm" ? 4 : 3)}>Back</Button>
            <Button variant="primary" disabled={submitting} onClick={submit}>{submitting ? "Uploading…" : "Upload & auto-match"}</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ad Spend upload sub-flow (PR #65). Pick a vendor + reporting period, attach
// one or more CSVs, preview each via /ad-spend/detect, then POST them all to
// /ad-spend/ingest (dry-run matcher; no license promotion). Surfside is
// snapshot-based and routes delivery vs conversions server-side; Dutchie needs
// an explicit period.
// ---------------------------------------------------------------------------
function AdSpendUpload({ slug, onBack }: { slug: string; onBack?: () => void }) {
  const toast = useToast();
  const base = `/api/brand/${encodeURIComponent(slug)}/ad-spend`;

  const [vendor, setVendor] = useState<AdSpendVendor>("dutchie");
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [detections, setDetections] = useState<DetectResult[]>([]);
  const [detecting, setDetecting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ uploaded: IngestRow[]; vendor: string } | null>(null);

  const periodRequired = vendor === "dutchie";
  const canIngest =
    files.length > 0 && !submitting && (!periodRequired || (!!periodStart && !!periodEnd));

  const onFiles = async (picked: File[]) => {
    setFiles(picked);
    setDetections([]);
    if (picked.length === 0) return;
    setDetecting(true);
    try {
      const out = await Promise.all(
        picked.map(async (f) => {
          const form = new FormData();
          form.append("file", f);
          try {
            return (await brandApi.postForm(`${base}/detect`, form)) as DetectResult;
          } catch {
            // Detect is a best-effort preview; a failure here doesn't block ingest.
            return { filename: f.name, detected_level: "account", vendor_guess: null, row_count: 0, columns: [] } as DetectResult;
          }
        })
      );
      setDetections(out);
      const guesses = new Set(out.map((d) => d.vendor_guess).filter(Boolean));
      if (guesses.size === 1 && !guesses.has(vendor)) {
        const guess = [...guesses][0] as AdSpendVendor;
        toast.push(`These files look like ${guess}. Switch the vendor if that's right.`, "error");
      }
    } finally {
      setDetecting(false);
    }
  };

  const ingest = async () => {
    if (!canIngest) return;
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append("vendor", vendor);
      if (periodStart) form.append("period_start", periodStart);
      if (periodEnd) form.append("period_end", periodEnd);
      for (const f of files) form.append("files", f);
      const r = await brandApi.postForm(`${base}/ingest`, form);
      setResult({ uploaded: r.uploaded || [], vendor: r.vendor || vendor });
      const totalRows = (r.uploaded || []).reduce((n: number, u: IngestRow) => n + (u.rows_ingested || 0), 0);
      toast.push(`Ingested ${r.uploaded?.length || 0} file(s) · ${totalRows} rows.`, "success");
    } catch (err) {
      toast.push(`Ingest failed: ${(err as Error).message}`, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setFiles([]);
    setDetections([]);
    setResult(null);
  };

  if (result) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Upload complete</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            {result.vendor} · {result.uploaded.length} file{result.uploaded.length === 1 ? "" : "s"} ingested. Matching ran in
            dry-run; no licenses were changed.
          </p>
        </div>
        <div className="overflow-x-auto rounded-md border border-gray-100">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="text-left px-3 py-2 font-medium">File</th>
                <th className="text-left px-3 py-2 font-medium">Level</th>
                <th className="text-right px-3 py-2 font-medium">Rows</th>
                <th className="text-right px-3 py-2 font-medium">Accounts</th>
              </tr>
            </thead>
            <tbody>
              {result.uploaded.map((u, i) => (
                <tr key={`${u.filename ?? "file"}-${i}`} className="border-t border-gray-100">
                  <td className="px-3 py-2 text-gray-800">{u.filename || "—"}</td>
                  <td className="px-3 py-2">
                    <Badge variant="neutral">{u.detected_level}</Badge>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-700">
                    {(u.rows_ingested || 0).toLocaleString()}
                    <span className="text-gray-400"> / {(u.rows_seen || 0).toLocaleString()}</span>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-700">{(u.vendor_account_count || 0).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={reset}>Upload more</Button>
          <Button variant="primary" onClick={() => (window.location.hash = `#/brand/${encodeURIComponent(slug)}/my-chains`)}>
            Done
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <Label htmlFor="vendor">Vendor</Label>
          <select
            id="vendor"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            value={vendor}
            onChange={(e) => setVendor(e.target.value as AdSpendVendor)}
          >
            {AD_SPEND_VENDORS.map((v) => (
              <option key={v.value} value={v.value}>{v.label}</option>
            ))}
          </select>
        </div>
        <div>
          <Label htmlFor="period_start" hint={periodRequired ? "required for Dutchie" : "optional"}>Period start</Label>
          <input
            id="period_start"
            type="date"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            value={periodStart}
            onChange={(e) => setPeriodStart(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="period_end" hint={periodRequired ? "required for Dutchie" : "optional"}>Period end</Label>
          <input
            id="period_end"
            type="date"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            value={periodEnd}
            onChange={(e) => setPeriodEnd(e.target.value)}
          />
        </div>
      </div>

      <div>
        <Label htmlFor="adspend_files">CSV or XLSX file(s)</Label>
        <input
          id="adspend_files"
          type="file"
          accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          multiple
          onChange={(e) => onFiles(Array.from(e.target.files ?? []))}
          className="mt-1 block w-full text-sm text-gray-700 file:mr-3 file:rounded-md file:border-0 file:bg-accent-500 file:px-3 file:py-1.5 file:text-white"
        />
        <p className="mt-1 text-xs text-gray-500">
          Surfside delivery + conversions exports (.csv or .xlsx) can be attached together; they're routed automatically.
        </p>
      </div>

      {detecting && <div className="text-sm text-gray-500">Inspecting files…</div>}

      {detections.length > 0 && (
        <div className="overflow-x-auto rounded-md border border-gray-100">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="text-left px-3 py-2 font-medium">File</th>
                <th className="text-left px-3 py-2 font-medium">Level</th>
                <th className="text-left px-3 py-2 font-medium">Looks like</th>
                <th className="text-right px-3 py-2 font-medium">Rows</th>
              </tr>
            </thead>
            <tbody>
              {detections.map((d, i) => (
                <tr key={`${d.filename ?? "file"}-${i}`} className="border-t border-gray-100">
                  <td className="px-3 py-2 text-gray-800">{d.filename || files[i]?.name || "—"}</td>
                  <td className="px-3 py-2"><Badge variant="neutral">{d.detected_level}</Badge></td>
                  <td className="px-3 py-2">
                    {d.vendor_guess ? (
                      <span className={d.vendor_guess === vendor ? "text-gray-600" : "text-amber-700"}>{d.vendor_guess}</span>
                    ) : (
                      <span className="text-gray-400">unknown</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-700">{(d.row_count || 0).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex gap-2">
        {onBack && <Button variant="ghost" onClick={onBack}>Back</Button>}
        <Button variant="primary" disabled={!canIngest} onClick={ingest}>
          {submitting ? "Uploading…" : "Upload & match"}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// API Exposure (PR #67). Three per-brand global toggles (cm / wtb / ad_spend)
// backed by brand.api_exposure — default off, each persists immediately. Shown
// on the onboarding landing so a brand opts in only once their mappings look
// right. WtbExposeToggle reuses expose_wtb and replaces the old per-column
// "expose via API" set on WTB uploads with a single global switch.
// ---------------------------------------------------------------------------

function ToggleSwitch({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors ${
        checked ? "bg-accent-500" : "bg-gray-300"
      } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-4" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}

const EXPOSURE_TOGGLES: { key: ExposureKey; label: string; hint: string }[] = [
  { key: "expose_cm", label: "Customer Master", hint: "Your accounts / stores list." },
  { key: "expose_wtb", label: "Where to Buy", hint: "Your public store locator feed." },
  { key: "expose_ad_spend", label: "Ad Spend", hint: "Dutchie / iHeartJane / Surfside spend." },
  { key: "expose_address_book", label: "Supplemental Data", hint: "Uploaded account / address-book / license lists." },
];

function ApiExposurePanel({ slug }: { slug: string }) {
  const toast = useToast();
  const { exposure, loading, error, setKey } = useExposure(slug);
  const [busy, setBusy] = useState<ExposureKey | null>(null);

  const flip = async (key: ExposureKey, value: boolean) => {
    setBusy(key);
    try {
      await setKey(key, value);
    } catch (err) {
      toast.push(`Could not update exposure: ${(err as Error).message}`, "error");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">API Exposure</h3>
        <p className="text-sm text-gray-600 mt-1">
          Controls whether your brand's data is included in API responses to your downstream tools. Default off. You can
          turn this on after you've confirmed your mappings on the Mappings pages.
        </p>
      </div>
      {error ? (
        <p className="text-sm text-red-600">Couldn't load exposure settings: {error}</p>
      ) : (
        <div className="divide-y divide-gray-100 border-t border-gray-100">
          {EXPOSURE_TOGGLES.map((t) => (
            <div key={t.key} className="flex items-center justify-between py-3">
              <div>
                <div className="text-sm font-medium text-gray-800">Expose {t.label} via API</div>
                <div className="text-xs text-gray-500">{t.hint}</div>
              </div>
              <ToggleSwitch
                checked={!!exposure?.[t.key]}
                disabled={loading || busy !== null}
                onChange={(v) => flip(t.key, v)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WtbExposeToggle({ slug }: { slug: string }) {
  const toast = useToast();
  const { exposure, loading, error, setKey } = useExposure(slug);
  const [busy, setBusy] = useState(false);

  const flip = async (value: boolean) => {
    setBusy(true);
    try {
      await setKey("expose_wtb", value);
    } catch (err) {
      toast.push(`Could not update exposure: ${(err as Error).message}`, "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-4 py-3">
      <div>
        <div className="text-sm font-medium text-gray-800">Expose this WTB upload in APIs</div>
        <div className="text-xs text-gray-500">
          {error
            ? `Couldn't load setting: ${error}`
            : "One switch governs all your Where-to-Buy data — replaces the old per-column API flags."}
        </div>
      </div>
      <ToggleSwitch checked={!!exposure?.expose_wtb} disabled={loading || busy || !!error} onChange={flip} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Data Contract & Coverage. Generated from live state (exposed datasets, opted-in
// columns, current coverage counts) via GET /api/brand/:slug/contract. The
// coverage table doubles as the cross-dataset "what's matched to what" review
// surface; the Download button pulls the same contract as a Markdown doc so a
// brand (DIY) or admin (managed service) never hand-writes a contract per brand.
// ---------------------------------------------------------------------------

type ContractDataset = {
  key: string;
  label: string;
  endpoint: string | null;
  exposed: boolean;
  status: "live" | "off" | "planned";
  coverage?: Record<string, number>;
  by_vendor?: Array<{ vendor: string; rows: number; store_resolved: number; chain_resolved: number }>;
  exposed_columns?: string[];
};
type BrandContract = { api_version: string; datasets: ContractDataset[] };

function coverageLabel(d: ContractDataset): string {
  if (d.by_vendor && d.by_vendor.length) {
    return d.by_vendor
      .map((v) => `${v.vendor}: ${v.rows} rows · ${v.store_resolved} store · ${v.chain_resolved} chain`)
      .join("  |  ");
  }
  if (d.coverage) {
    const rows = d.coverage.rows ?? 0;
    const resolved = d.coverage.resolved;
    if (resolved == null) return `${rows} rows`;
    const pct = rows > 0 ? Math.round((resolved / rows) * 100) : 0;
    return `${rows} rows · ${resolved} resolved (${pct}%)`;
  }
  return "—";
}

function DataContractPanel({ slug }: { slug: string }) {
  const toast = useToast();
  const [contract, setContract] = useState<BrandContract | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    brandApi
      .get(`/api/brand/${encodeURIComponent(slug)}/contract`)
      .then((r) => {
        if (live) setContract(r as BrandContract);
      })
      .catch((err) => {
        if (live) setError((err as Error).message);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [slug]);

  const download = async () => {
    setDownloading(true);
    try {
      const md = await brandApi.get(`/api/brand/${encodeURIComponent(slug)}/contract?format=markdown`);
      const blob = new Blob([typeof md === "string" ? md : JSON.stringify(md, null, 2)], {
        type: "text/markdown;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `storesync-data-contract-${slug}.md`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.push(`Could not download contract: ${(err as Error).message}`, "error");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Data Contract &amp; Coverage</h3>
          <p className="text-sm text-gray-600 mt-1">
            Generated live from what you've uploaded and exposed. Coverage shows how many rows resolved to a canonical
            license — your at-a-glance review of what's matched across datasets.
          </p>
        </div>
        <button
          type="button"
          onClick={download}
          disabled={downloading || loading || !!error}
          className="flex-shrink-0 rounded-md bg-accent-500 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {downloading ? "Preparing…" : "Download (Markdown)"}
        </button>
      </div>
      {loading ? (
        <p className="text-sm text-gray-500">Loading coverage…</p>
      ) : error ? (
        <p className="text-sm text-red-600">Couldn't load contract: {error}</p>
      ) : contract ? (
        <div className="overflow-x-auto border-t border-gray-100">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="py-2 pr-4">Dataset</th>
                <th className="py-2 pr-4">Exposed</th>
                <th className="py-2 pr-4">Coverage</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {contract.datasets.map((d) => (
                <tr key={d.key}>
                  <td className="py-2 pr-4">
                    <div className="font-medium text-gray-800">{d.label}</div>
                    {d.exposed_columns && d.exposed_columns.length > 0 && (
                      <div className="text-xs text-gray-500">+{d.exposed_columns.length} custom column(s)</div>
                    )}
                  </td>
                  <td className="py-2 pr-4 whitespace-nowrap">
                    {d.status === "planned" ? (
                      <span className="text-gray-400">planned</span>
                    ) : d.exposed ? (
                      <span className="text-green-600">✓ on</span>
                    ) : (
                      <span className="text-gray-400">off</span>
                    )}
                  </td>
                  <td className="py-2 pr-4 text-gray-700">{coverageLabel(d)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
